import React, { useEffect } from "react";

import { useLocation } from "react-router";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import BillingLoadFailure from "@app/analytics/account/Billing/components/BillingLoadFailure";
import useBillingCustomer from "@app/analytics/account/Billing/hooks/useBillingCustomer";
import useCheckout from "@app/analytics/account/Billing/hooks/useCheckout";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";

import LoadSpinner from "@rui/LoadSpinner";
import Stack from "@rui/Stack";

import BillingManage from "./BillingManage";
import BillingPlanCompare from "./BillingPlanCompare";
import BillingUpdatePaymentMethod from "./BillingUpdatePaymentMethod";
import BillingCreditCheckout from "./checkout/BillingCreditCheckout";
import BillingSubscriptionCheckout from "./checkout/BillingSubscriptionCheckout";
import { BillingWrapper } from "./components/styles";

const Billing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { plansInitialLoading } = useBillingContext();
  const { cancelUploadCreditsCheckout } = useCheckout();
  const {
    isLoading: isBillingCustomerLoading,
    isError: billingCustomerIsError,
  } = useBillingCustomer();
  const { canEditBillingPlan } = useFeatureAccess();

  useEffect(() => {
    if (!canEditBillingPlan) return;
    // clear any past credit checkout false starts when the account owner views the billing page
    cancelUploadCreditsCheckout.mutate();
  }, [canEditBillingPlan]);

  useEffect(() => {
    if (canEditBillingPlan) return;
    if (location.pathname != ROUTES.BILLING) {
      navigate(ROUTES.BILLING, { replace: true });
    }
  }, [canEditBillingPlan, location.pathname]);

  const isError = billingCustomerIsError;
  const isLoading = plansInitialLoading || isBillingCustomerLoading;

  return (
    <BillingWrapper flexGrow={1}>
      {isLoading ? (
        <Stack sx={{ height: "100%" }}>
          <LoadSpinner sx={{ margin: "auto" }} />
        </Stack>
      ) : isError ? (
        <Stack sx={{ p: 2 }}>
          <BillingLoadFailure />
        </Stack>
      ) : (
        <Routes>
          <Route path="/" element={<BillingManage />} />
          <Route path="/compare-plans" element={<BillingPlanCompare />} />
          <Route path="/checkout/credits" element={<BillingCreditCheckout />} />
          <Route
            path="/checkout/subscription"
            element={<BillingSubscriptionCheckout />}
          />
          <Route path="/update" element={<BillingUpdatePaymentMethod />} />
          <Route path="*" element={<Navigate to={ROUTES.BILLING} />} />
        </Routes>
      )}
    </BillingWrapper>
  );
};

export default Billing;
